import React from 'react';

import { isEmpty } from 'lodash';

import type { PredefinedPrompt } from '~/components/Chat/types';
import Button from '~/core/atoms/Buttons/Button';
import IconButton from '~/core/atoms/Buttons/IconButton';
import DropdownMenu from '~/core/atoms/DropdownMenu';
import Tags from '~/core/atoms/Tag/Tags';
import { TagData } from '~/core/atoms/Tag/types';
import TextArea from '~/core/atoms/TextArea';
import PlusIcon from '~/core/iconComponents/icons/PlusIcon.tsx';
import SendMessageIcon from '~/core/iconComponents/icons/SendMessageIcon';
import cn from '~/utils/cn.ts';

export interface ChatInputAction {
  key: string;
  icon: React.ReactNode;
  onClick: () => void;
  ariaLabel?: string;
  disabled?: boolean;
}

interface ChatInputProps {
  value: string;
  rows: number;
  disabled?: boolean;
  disableSubmit?: boolean;
  placeholder?: string;
  onTextChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSubmit: (text: string) => void;
  className?: string;
  actions?: ChatInputAction[];
  tags?: TagData[];
  onTagClose?: (tagId: string | number) => void;
  predefinedPrompts?: PredefinedPrompt[];
  onPromptSelect?: (promptKey: string) => void;
}

const ChatInput: React.FC<ChatInputProps> = ({
  value,
  rows,
  disabled = false,
  disableSubmit = false,
  placeholder,
  onTextChange,
  onSubmit,
  className,
  actions = [],
  tags = [],
  onTagClose = () => null,
  predefinedPrompts = [],
  onPromptSelect,
}) => {
  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isEmpty(value)) {
        onSubmit(value);
      }
    }
  };

  // Build dropdown menu groups from actions and predefined prompts
  const dropdownGroups = React.useMemo(() => {
    const groups = [];

    // Add actions group if actions exist
    if (!isEmpty(actions)) {
      groups.push({
        items: actions.map((action) => ({
          label: action.ariaLabel || action.key,
          icon: action.icon,
          onClick: action.onClick,
          disabled: action.disabled,
        })),
      });
    }

    // Add predefined prompts group if prompts exist
    if (!isEmpty(predefinedPrompts) && onPromptSelect) {
      groups.push({
        title: "Clive's suggestions",
        items: predefinedPrompts.map((prompt) => ({
          label: prompt.prompt_title,
          onClick: () => onPromptSelect(prompt.prompt_key),
          disabled: false,
        })),
      });
    }

    return groups;
  }, [actions, predefinedPrompts, onPromptSelect]);

  return (
    <div className={cn('rounded-lg bg-white p-4 shadow-sm', className)}>
      {!isEmpty(tags) ? (
        <Tags tags={tags} shouldDisplayClose onTagClose={onTagClose} className="bg-transparent px-12 pt-20" />
      ) : null}
      <div className="inline-flex w-full items-center">
        <div>
          {!isEmpty(dropdownGroups) && (
            <DropdownMenu
              customMenuButton={
                <IconButton
                  size="md"
                  className="items-center justify-center rounded-full bg-blue-100 p-8 hover:bg-blue-200"
                  aria-label="Chat options menu"
                >
                  <PlusIcon className="text-blue-900" size={18} />
                </IconButton>
              }
              groups={dropdownGroups}
              menuClassName="h-full inline-flex items-center ml-8"
              itemClassName="w-[400px]"
              anchor="top"
              align="left"
            />
          )}
        </div>

        <TextArea
          rows={rows}
          resizable={false}
          value={value}
          onChange={onTextChange}
          className="semibold rounded-xl bg-transparent py-[22px] pl-16 text-sm focus:outline-none"
          placeholder={placeholder}
          disabled={disabled}
          onKeyDown={onKeyDown}
          autoResize
          maxHeight={300}
        />
        <div className="flex-end mr-8 flex items-center justify-center rounded-full">
          <Button
            disabled={disabled || disableSubmit}
            onClick={() => onSubmit(value)}
            className="flex h-40 w-40 items-center justify-center rounded-full bg-pink-600 p-8 hover:bg-pink-700 disabled:bg-pink-500 disabled:hover:bg-pink-600/50"
          >
            <SendMessageIcon className="h-40 w-40 p-2" iconColor="white" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChatInput;
